<script setup lang="ts">
import { ref, computed } from 'vue'
import * as Icons from '@element-plus/icons-vue'
import { Fold, Expand, Menu, ArrowDown } from '@element-plus/icons-vue'
import { DEFAULT_APP_TITLE } from '@/config/constants'
import { RouteRecordRaw, useRoute, useRouter } from 'vue-router'
import { STATIC_ROUTES } from '@/routers/routes-static'
import { useUserStore } from '@/stores/user'

const route = useRoute()
const router = useRouter()
const user = useUserStore()

const collapsed = ref(false)

const layoutRoute = computed(() => STATIC_ROUTES.find((r) => r.path === '/') as RouteRecordRaw)
const menuChildren = computed(() => (layoutRoute.value?.children || []).filter((c) => !(c.meta?.hidden)))

const active = computed(() => route.path)

function iconComp(name?: string) {
  if (!name) return null
  const c = (Icons as Record<string, unknown>)[name]
  return typeof c === 'object' && c ? (c as typeof Icons.Odometer) : null
}

function go(path: string) {
  router.push(path)
}

async function logout() {
  user.logout()
  await router.replace({ path: '/login' })
}

const userLetter = computed(() => {
  const n = (user.username || 'U').trim()
  return n.slice(0, 1).toUpperCase()
})

const FallbackIcon = Menu
</script>

<template>
  <div class="gk-layout">
    <el-container class="gk-layout__wrap">
      <el-aside class="gk-layout__aside" :width="collapsed ? '72px' : '240px'">
        <div class="gk-layout__brand">
          <span class="gk-layout__logo">贷</span>
          <transition name="el-fade-in">
            <span v-show="!collapsed" class="gk-layout__brand-text">{{ DEFAULT_APP_TITLE }}</span>
          </transition>
        </div>
        <el-scrollbar class="gk-layout__menu-scroll">
          <el-menu
            :default-active="active"
            class="gk-layout__menu"
            :collapse="collapsed"
            :collapse-transition="false"
            background-color="transparent"
            text-color="rgba(226, 232, 240, 0.92)"
            active-text-color="#fff"
            router
          >
            <template v-for="child in menuChildren" :key="child.path">
              <el-menu-item
                v-if="child.path && !child.redirect"
                :index="`/${child.path}`"
                class="gk-layout__menu-item"
                @click="go(`/${child.path}`)"
              >
                <el-icon class="gk-layout__menu-icon">
                  <component :is="iconComp(child.meta?.icon as string) || FallbackIcon" />
                </el-icon>
                <template #title>{{ child.meta?.title || child.path }}</template>
              </el-menu-item>
            </template>
          </el-menu>
        </el-scrollbar>
        <div class="gk-layout__aside-foot" v-show="!collapsed">
          <span class="gk-layout__ver">Console</span>
        </div>
      </el-aside>
      <el-container class="gk-layout__right">
        <el-header class="gk-layout__header">
          <div class="gk-layout__header-left">
            <button type="button" class="gk-layout__icon-btn" aria-label="toggle menu" @click="collapsed = !collapsed">
              <el-icon :size="20">
                <Fold v-if="!collapsed" />
                <Expand v-else />
              </el-icon>
            </button>
            <el-breadcrumb separator="/" class="gk-layout__crumb">
              <el-breadcrumb-item :to="{ path: '/home' }">工作台</el-breadcrumb-item>
              <el-breadcrumb-item v-if="route.path !== '/home'">
                {{ route.meta?.title || '—' }}
              </el-breadcrumb-item>
            </el-breadcrumb>
          </div>
          <div class="gk-layout__header-right">
            <el-dropdown trigger="click" placement="bottom-end">
              <span class="gk-layout__user-chip">
                <el-avatar :size="34" class="gk-layout__avatar">{{ userLetter }}</el-avatar>
                <span class="gk-layout__uname">{{ user.username || 'admin' }}</span>
                <el-icon class="gk-layout__caret"><ArrowDown /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item disabled>角色 · 管理员</el-dropdown-item>
                  <el-dropdown-item divided @click="logout">退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </el-header>
        <el-main class="gk-layout__main">
          <div class="gk-layout__main-inner">
            <RouterView v-slot="{ Component }">
              <transition mode="out-in" name="el-fade-in-linear">
                <component :is="Component" />
              </transition>
            </RouterView>
          </div>
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<style scoped lang="scss">
.gk-layout {
  height: 100%;
}

.gk-layout__wrap {
  height: 100vh;
  overflow: hidden;
}

.gk-layout__aside {
  background: linear-gradient(165deg, #0f172a 0%, #1e293b 48%, #0f172a 100%);
  transition: width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
}

.gk-layout__brand {
  height: 58px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  color: #f8fafc;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-weight: 700;
  letter-spacing: 0.04em;
}

.gk-layout__logo {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: #fff;
  flex-shrink: 0;
  font-weight: 800;
}

.gk-layout__brand-text {
  font-size: 13px;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.95;
}

.gk-layout__menu-scroll {
  flex: 1;
  padding: 10px 8px 12px;
}

.gk-layout__menu {
  border-right: none;
}

.gk-layout__menu-item {
  border-radius: 10px;
  margin-bottom: 4px;
  height: 44px !important;
  line-height: 44px !important;
}

.gk-layout__menu-item:hover {
  background: rgba(255, 255, 255, 0.08) !important;
}

.gk-layout__menu :deep(.el-menu-item.is-active) {
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.35), rgba(59, 130, 246, 0.08)) !important;
  color: #fff !important;
  font-weight: 600;
}

.gk-layout__menu-icon {
  font-size: 18px;
}

.gk-layout__aside-foot {
  padding: 10px 16px 14px;
  font-size: 11px;
  color: rgba(148, 163, 184, 0.85);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.gk-layout__ver {
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.gk-layout__right {
  min-width: 0;
  background: var(--ep-surface-page, #f1f5f9);
}

.gk-layout__header {
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--el-border-color-lighter);
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.04);
}

.gk-layout__header-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.gk-layout__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: var(--el-text-color-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.gk-layout__icon-btn:hover {
  background: #e2e8f0;
}

.gk-layout__crumb {
  font-size: 13px;
  min-width: 0;
}

.gk-layout__crumb :deep(.el-breadcrumb__inner) {
  font-weight: 500;
}

.gk-layout__header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gk-layout__user-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 6px 10px 6px 6px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid var(--el-border-color-lighter);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.gk-layout__user-chip:hover {
  border-color: var(--el-color-primary-light-5);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.gk-layout__avatar {
  background: linear-gradient(135deg, #2563eb, #1d4ed8) !important;
  color: #fff !important;
  font-weight: 700;
  font-size: 14px !important;
}

.gk-layout__uname {
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gk-layout__caret {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.gk-layout__main {
  padding: 0;
  overflow: auto;
}

.gk-layout__main-inner {
  min-height: calc(100vh - 58px);
  padding: 20px 24px 28px;
}
</style>
